/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { GlobalPositionStrategy, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { SelectionState } from '@map/state/selection.state';
import { RouterNavigation } from '@ngxs/router-plugin';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { isEqual } from 'lodash-es';
import { Observable, of, Subject } from 'rxjs';
import {
    concatMap,
    distinctUntilChanged,
    filter,
    first,
    mergeMap,
    switchMap,
    switchMapTo,
    take,
    takeUntil,
    withLatestFrom
} from 'rxjs/operators';
import { AuthState } from '../../../core/security/state/auth.state';
import { DashGisOverlay } from '../../../core/ui/overlay/dash-gis-overlay.service';
import { MapConfigService } from '../../map/services/map-config.service';
import { MapCreated, MapDestroyed } from '../../map/state/active-map.actions';
import { MapConfigState } from '../../map/state/map-config.state';
import { SelectionFeatures } from '../../map/state/selection.model';
import { LoggerService } from '../../shared/services/logger.service';
import { DataReportDisplayComponent } from '../reportDisplay/data-report-display.component';
import { FeatureReportDisplayComponent } from '../reportDisplay/feature-report-display.component';
import { ReportDisplayOverlayRef } from '../reportDisplay/report-display.overlay-ref';
import { ClearReports, LoadFeatureReports, SetActiveFeatureReports } from '../state/report.actions';
import { DataReportCollection, FeatureReportCollection, LoadFeatureReportsPayload, ReportTypeValue } from '../state/report.model';
import { ReportState } from '../state/report.state';

interface ReportDisplayConfig {
    width?: string;
    maxWidth?: string;
    hasBackdrop: boolean;
    panelClass: string;
    positionStrategy: GlobalPositionStrategy;
}

@Injectable()
export class ReportDisplayService {
    public activeFeatureReports$: Observable<FeatureReportCollection> = inject(Store).select(ReportState.activeFeatureReports);
    public activeDataReports$: Observable<DataReportCollection> = inject(Store).select(ReportState.activeDataReports);
    public loadingFeatureReports$: Observable<boolean> = inject(Store).select(ReportState.loadingFeatureReports);

    public route$ = new Subject<string>();

    private featureOverlayRef: OverlayRef = null;
    private dataOverlayRef: OverlayRef = null;

    private validated$: Observable<boolean> = inject(Store).select(AuthState.validated);
    private selection$: Observable<SelectionFeatures> = inject(Store).select(SelectionState.selection);
    private mapConfigLoaded$: Observable<boolean> = inject(Store).select(MapConfigState.loaded);

    private readonly dataReportOnMapConfig: ReportDisplayConfig;
    private readonly featureReportOnMapConfig: ReportDisplayConfig;
    private readonly reportOnDashboardConfig: ReportDisplayConfig;

    constructor(
        private logger: LoggerService,
        private mapConfig: MapConfigService,
        private overlay: DashGisOverlay,
        private dialog: MatDialog,
        private injector: Injector,
        private router: Router,
        private actions$: Actions,
        private store: Store
    ) {
        this.dataReportOnMapConfig = {
            width: '900px',
            hasBackdrop: false,
            panelClass: 'dash-gis-floating-panel',
            positionStrategy: this.overlay.position().global().left('240px').top('160px')
        };
        this.featureReportOnMapConfig = {
            width: '450px',
            hasBackdrop: false,
            panelClass: 'dash-gis-floating-panel',
            positionStrategy: this.overlay.position().global().right('45px').top('110px')
        };
        this.reportOnDashboardConfig = {
            maxWidth: '90vw',
            hasBackdrop: false,
            panelClass: 'dash-gis-floating-panel',
            positionStrategy: this.overlay.position().global().centerHorizontally().top('140px')
        };
        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            filter(e => !!e)
        ).subscribe({
            next: (e: NavigationEnd) => this.route$.next(e.url)
        });
    }

    init() {
        // load feature report definitions
        this.mapConfigLoaded$
            .pipe(
                distinctUntilChanged(),
                filter((b) => b),
                switchMap(() => this.mapConfig.getAllSelectableLayerNames$()),
                distinctUntilChanged(isEqual),
                switchMap((layerNames: string[]) => {
                    const payload: LoadFeatureReportsPayload = {
                        layerNames,
                        mapName: this.mapConfig.mapName
                    };
                    return this.store.dispatch(new LoadFeatureReports(payload));
                })
            )
            .subscribe({
                next: () => {},
                error: (err) => this.logger.error('Load feature reports subscription', err)
            });

        this.actions$
            .pipe(
                // Map has been created
                ofActionSuccessful(MapCreated),
                // User is validated
                switchMapTo(this.validated$.pipe(distinctUntilChanged(), filter((b) => b))),
                // When selection changes
                concatMap(() => this.selection$),
                // Wait for any feature reports loading
                switchMap(() => this.loadingFeatureReports$.pipe(distinctUntilChanged(), filter((b) => !b))),
                // get the latest selection (once)
                concatMap(() => this.selection$.pipe(take(1))),
                concatMap((selection) =>
                    this.store.dispatch(
                        new SetActiveFeatureReports({
                            selection,
                            mapName: this.mapConfig.mapName
                        })
                    )
                )
            )
            .subscribe({
                next: () => {},
                error: (err) => this.logger.error(err)
            });

        // show feature reports on selection change or change in current active reports
        this.actions$
            .pipe(
                ofActionSuccessful(SetActiveFeatureReports),
                mergeMap(() => this.activeFeatureReports$),
                distinctUntilChanged(isEqual),
                filter((activeReports) => {
                    let valid = true;
                    const layers = Object.getOwnPropertyNames(activeReports);
                    if (layers.length === 0) {
                        valid = false;
                    } else {
                        for (let i = 0; i < layers.length; i++) {
                            const layer = layers[i];
                            if (activeReports[layer].length === 0) {
                                valid = false;
                                break;
                            }
                        }
                    }
                    return valid;
                }),
                withLatestFrom(this.route$)
            )
            .subscribe({
                next: ([ , routerState ]) => this.displayReport('feature', routerState),
                error: (err) => this.logger.error(err)
            });

        this.activeDataReports$
            .pipe(
                filter((dataReports) => dataReports.length > 0),
                withLatestFrom(this.route$)
            )
            .subscribe({
                next: ([ , routerState ]) => this.displayReport('data', routerState),
                error: (err) => this.logger.error(err)
            });

        this.selection$
            .pipe(
                filter((selection) => isEqual(selection, {})),
                takeUntil(this.actions$.pipe(ofActionSuccessful(MapDestroyed)))
            )
            .subscribe({
                next: () => this.closeReportPanels(),
                error: (err) => this.logger.error(err)
            });
    }

    setReportWidth(type: ReportTypeValue, width: string): void {
        let ref;
        if (type === 'feature') {
            ref = this.featureOverlayRef;
        } else if (type === 'data') {
            ref = this.dataOverlayRef;
        }
        ref.updateSize({ width });
        ref.updatePosition();
    }

    private displayReport(reportType: ReportTypeValue, url: string): void {
        const page = url.match(/^\/(\w+)/)[1];
        switch (reportType) {
            case 'feature':
                if (this.featureOverlayRef) {
                    if (this.featureOverlayRef.hostElement) {
                        return;
                    } else {
                        this.closeReportPanel('feature');
                    }
                }
                this.displayFeatureReports(page);
                break;

            case 'data':
                if (this.dataOverlayRef) {
                    if (this.dataOverlayRef.hostElement) {
                        return;
                    } else {
                        this.closeReportPanel('data');
                    }
                }
                this.displayDataReports(page);
                break;
        }
    }

    private displayFeatureReports(page: string) {
        const overlayConfig = page === 'dashboard' ? this.reportOnDashboardConfig : this.featureReportOnMapConfig;
        this.featureOverlayRef = this.overlay.create(overlayConfig);
        const reportDisplayOverlayRef = new ReportDisplayOverlayRef(this.featureOverlayRef);
        const injector = this.createInjector(reportDisplayOverlayRef);

        const portal = new ComponentPortal(FeatureReportDisplayComponent, null, injector);
        const panelRef = this.featureOverlayRef.attach<FeatureReportDisplayComponent>(portal);
        panelRef.instance.closePanel.subscribe(() => {
            // panelRef.instance.close();
            // panelRef.destroy();
            this.closeReportPanel('feature');
            this.store.dispatch(new ClearReports({ type: 'feature' }));
        });

        this.featureOverlayRef
            .detachments()
            .pipe(takeUntil(this.actions$.pipe(ofActionSuccessful(MapDestroyed))))
            .subscribe();

        this.actions$
            .pipe(ofActionSuccessful(RouterNavigation), first())
            .subscribe(() => reportDisplayOverlayRef.close());
    }

    private displayDataReports(page: string) {
        const overlayConfig = page === 'dashboard' ? this.reportOnDashboardConfig : this.dataReportOnMapConfig;
        this.dataOverlayRef = this.overlay.create(overlayConfig);
        const reportDisplayOverlayRef = new ReportDisplayOverlayRef(this.dataOverlayRef);
        const injector = this.createInjector(reportDisplayOverlayRef);
        const portal = new ComponentPortal(DataReportDisplayComponent, null, injector);
        const panelRef = this.dataOverlayRef.attach<DataReportDisplayComponent>(portal);
        panelRef.instance.closePanel.pipe(take(1)).subscribe(() => {
            // panelRef.instance.close();
            // panelRef.destroy();
            this.closeReportPanel('data');
            this.store.dispatch(new ClearReports({ type: 'data' }));
        });

        this.actions$
            .pipe(ofActionSuccessful(RouterNavigation), first())
            .subscribe(() => reportDisplayOverlayRef.close());
    }

    private createInjector(reportOverlayRef: ReportDisplayOverlayRef) {
        return Injector.create({
            parent: this.injector,
            providers: [
                {
                    provide: ReportDisplayOverlayRef,
                    useValue: reportOverlayRef
                }
            ]
        });
    }

    private closeReportPanels() {
        this.closeReportPanel('feature');
        this.closeReportPanel('data');
    }

    private closeReportPanel(type: string) {
        switch (type) {
            case 'feature':
                if (this.featureOverlayRef) {
                    if (this.featureOverlayRef.hostElement) {
                        this.featureOverlayRef.detach();
                    }
                    this.featureOverlayRef.dispose();
                }
                this.featureOverlayRef = null;
                break;
            case 'data':
                if (this.dataOverlayRef) {
                    if (this.dataOverlayRef.hostElement) {
                        this.dataOverlayRef.detach();
                    }
                    this.dataOverlayRef.dispose();
                }
                this.dataOverlayRef = null;
                break;
        }
    }
}
